import { api } from "../../../api/http";

const JOB_STATUS_NUMBER_MAP = {
  0: "PENDING",
  1: "RUNNING",
  2: "SUCCESS",
  3: "FAILED",
  4: "CANCELLED"
};

const normalizeStatus = (status) => {
  if (typeof status === "number") {
    return JOB_STATUS_NUMBER_MAP[status] ?? `${status}`;
  }

  if (typeof status === "string" && status.length > 0) {
    return status.toUpperCase();
  }

  return "PENDING";
};

const normalizeJob = (raw) => {
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!raw) {
    return null;
  }

  return {
    id: raw.id ?? raw.Id,
    name: raw.name ?? raw.Name ?? "Unknown job",
    type: raw.type ?? raw.Type,
    status: normalizeStatus(raw.status ?? raw.Status),
    progress: Number(raw.progress ?? raw.Progress ?? 0),
    createdAt: raw.createdAt ?? raw.CreatedAt,
    lastMessage: raw.lastMessage ?? raw.LastMessage ?? null
  };
};

const safeParseJson = (value) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  return value;
};

const parseJobsData = (value) => {
  const payload = safeParseJson(value);
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map(normalizeJob)
    .filter((job) => job !== null);
};

export const jobService = {
  async getJobs(pageNumber = 1, pageSize = 10) {
    const queryParams = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize)
    }).toString();

    const json = await api.get(`/api/Jobs?${queryParams}`);
    if (!json || !json.success) {
      return {
        jobs: [],
        pageNumber,
        pageSize,
        totalCount: 0
      };
    }

    const payload = safeParseJson(json.data) ?? {};

    const normalizePayload = (value) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
      }
      return Object.entries(value).reduce((acc, [key, val]) => {
        const normalizedKey =
          !key || key.length === 0
            ? key
            : key[0].toLowerCase() + key.slice(1);
        acc[normalizedKey] = val;
        return acc;
      }, {});
    };

    const normalizedPayload = normalizePayload(payload);
    const items = parseJobsData(normalizedPayload.items ?? []);
    const normalizeNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const responsePageNumber = normalizeNumber(normalizedPayload.pageNumber) ?? pageNumber;
    const responsePageSize = normalizeNumber(normalizedPayload.pageSize) ?? pageSize;
    const responseTotalCount = normalizeNumber(normalizedPayload.totalCount);

    return {
      jobs: items,
      pageNumber: Math.max(1, responsePageNumber),
      pageSize: Math.max(1, responsePageSize),
      totalCount: responseTotalCount !== null
        ? Math.max(0, responseTotalCount)
        : items.length
    };
  },

  async createJob(name, type, categoryIds = null) {
    const json = await api.post("/api/Jobs/create", { name, type, resourceIds: categoryIds });
    if (!json || !json.success) {
      return null;
    }

    return normalizeJob(json.data);
  },

  async getJobById(id) {
    if (!id) {
      return null;
    }

    const json = await api.get(`/api/Jobs/${id}`);
    if (!json || !json.success) {
      return null;
    }

    return normalizeJob(json.data);
  },

  cancelJob(id) {
    return api.post(`/api/Jobs/${id}/cancel`);
  },

  retryJob(id) {
    return api.post(`/api/Jobs/${id}/retry`);
  }
};
